import * as React from 'react';
import {
  Badge,
  Box,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import Grid from '@mui/material/Unstable_Grid2'; // Grid version 2

import Row from './Row';
import FilterListIcon from '@mui/icons-material/FilterList';

import {useForm, FormProvider} from 'react-hook-form';
import Form from './form/Form';
import FormTextField from './form/FormTextField';
import FormDropDown from './form/FormDropDown';
import FormMultiSelectChips from './form/FormMultiSelectChip';
import FormButton from './form/FormButton';
import FormDateTimePicker from './form/FormDateTimePicker';
import FormDatePicker from './form/FormDatePicker';

import { dayjs } from '../utils/datetime';

const isDayJs = dayjs.isDayjs;
const isDateFilterType = (type) => (
  type === 'date' ||
  type === 'date-before' ||
  type === 'date-after' ||
  type === 'datetime' ||
  type === 'datetime-before' ||
  type === 'datetime-after'
);

const getComparableValue = (value, {treatAsDate = false} = {}) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (dayjs.isDayjs(value)) return value.valueOf();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (treatAsDate) {
      const parsed = dayjs(trimmed);
      if (parsed.isValid()) return parsed.valueOf();
    }
    return trimmed.toLowerCase();
  }
  if (treatAsDate) {
    const parsed = dayjs(value);
    if (parsed.isValid()) return parsed.valueOf();
  }
  return String(value).toLowerCase();
};

const compareValues = (a, b, options = {}) => {
  const aValue = getComparableValue(a, options);
  const bValue = getComparableValue(b, options);

  if (aValue === null && bValue === null) return 0;
  if (aValue === null) return -1;
  if (bValue === null) return 1;
  if (typeof aValue === 'number' && typeof bValue === 'number') return aValue - bValue;
  return String(aValue).localeCompare(String(bValue));
};

function Filter({records, setFilteredRecords, filterOptions, sortOptions, badgeColor = 'secondary'}) {

  const [isOpen, setIsOpen] = React.useState(false);
  const [isActive, setIsActive] = React.useState(false);

  const applySort = React.useCallback((list, sortKey, sortDirection) => {
    const sortOption = sortOptions.find((option) => option.key === sortKey);
    const relatedFilterOption = filterOptions.find((option) => (
      option.key === sortKey || option.recordkey === sortKey
    ));
    const sortType = sortOption?.type || relatedFilterOption?.type;
    const compareOptions = {treatAsDate: isDateFilterType(sortType)};

    if (sortOption) {
      list.sort((a, b) => {
        const aValue = sortOption?.getValue ? sortOption.getValue(a[sortKey], a) : a[sortKey];
        const bValue = sortOption?.getValue ? sortOption.getValue(b[sortKey], b) : b[sortKey];
        const comparison = compareValues(aValue, bValue, compareOptions);
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }
  }, [filterOptions, sortOptions]);

  const defaultValues = filterOptions.reduce((acc, cur) => {
    switch(cur.type) {
      case 'date':
      case 'date-before':
      case 'date-after':
      case 'datetime':
      case 'datetime-before':
      case 'datetime-after':
        acc[cur.key] = null;
        break;
      case 'multi-select':
        acc[cur.key] = [];
        break;
      default:
        acc[cur.key] = '';
        break;
    }
    return acc;
  }, {__sortkey: '', __sortdirection: 'asc'});

  const defaultSortOption = sortOptions.find((option) => (option.default === 'asc' || option.default === 'desc'));
  if (defaultSortOption) {
    defaultValues.__sortkey = defaultSortOption.key;
    defaultValues.__sortdirection = String(defaultSortOption.default);
  }

  const methods = useForm({defaultValues});

  React.useEffect(() => {
    const tempRecords = [...records];
    if (defaultSortOption) {
      applySort(tempRecords, defaultSortOption.key, String(defaultSortOption.default));
      setIsActive(true);
    } else {
      setIsActive(false);
    }
    setFilteredRecords(tempRecords);
  }, [applySort, defaultSortOption, records, setFilteredRecords]);

  const handleSubmit = React.useCallback(async(inputData) => {

    const filteredRecords = records.filter((record) => {

      const filterOptionResults = filterOptions.map((cur) => {
        const recordKey = cur.recordkey || cur.key;
        const recordValue = (typeof(cur.getValue) === 'function') ? cur.getValue(record[recordKey], record) : record[recordKey];
        const filterValue = inputData[cur.key];

        if (!filterValue || String(filterValue).trim() === '') return true;
        if (recordValue === null || recordValue === undefined) return false;

        switch (cur.type) {
          case 'text':
            return String(recordValue).toLowerCase().includes(String(filterValue).toLowerCase());
          case 'multi-select':
            return Array.isArray(filterValue) && filterValue.includes(recordValue);
          case 'date':
            return ((isDayJs(recordValue)) ? recordValue : dayjs(recordValue)).startOf('day').isSame(
              ((isDayJs(filterValue)) ? filterValue : dayjs(String(filterValue))).startOf('day')
            );
          case 'datetime':
            return ((isDayJs(recordValue)) ? recordValue : dayjs(recordValue)).startOf('minute').isSame(
              ((isDayJs(filterValue)) ? filterValue : dayjs(String(filterValue))).startOf('minute')
            );
          case 'date-after':
          case 'datetime-after':
            return ((isDayJs(recordValue)) ? recordValue : dayjs(recordValue)).isAfter(
              ((isDayJs(filterValue)) ? filterValue : dayjs(String(filterValue)))
            ) || ((isDayJs(recordValue)) ? recordValue : dayjs(recordValue)).isSame(
              ((isDayJs(filterValue)) ? filterValue : dayjs(String(filterValue)))
            );
          case 'date-before':
          case 'datetime-before':
            return ((isDayJs(recordValue)) ? recordValue : dayjs(recordValue)).isBefore(
              ((isDayJs(filterValue)) ? filterValue : dayjs(String(filterValue)))
            ) || ((isDayJs(recordValue)) ? recordValue : dayjs(recordValue)).isSame(
              ((isDayJs(filterValue)) ? filterValue : dayjs(String(filterValue)))
            );
          case 'single-select':
          default:
            return filterValue === recordValue;
        }
      });

      return !filterOptionResults.includes(false);
    });

    if (records.length !== filteredRecords.length) {
      setIsActive(true);
    } else {
      setIsActive(false);
    }

    if (inputData.__sortkey) {
      const sortKey = String(inputData.__sortkey);
      const sortDirection = String(inputData.__sortdirection);
      applySort(filteredRecords, sortKey, sortDirection);
      setIsActive(true);
    }

    setFilteredRecords([...filteredRecords]);
  }, [applySort, filterOptions, records, setFilteredRecords]);

  const handleChange = React.useCallback((inputData) => {
    handleSubmit(inputData);
  }, [handleSubmit]);

  const resetFilters = () =>{
    setIsActive(false);
    methods.reset();

    const tempRecords = [...records];
    if (defaultSortOption) {
      applySort(tempRecords, defaultSortOption.key, String(defaultSortOption.default));
      setIsActive(true);
    }
    setFilteredRecords(tempRecords);
  };

  const sortkeyItems = sortOptions.map((cur) => {
    return {
      value: cur.key,
      label: cur.label || cur.key,
    };
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Badge color={badgeColor} variant="dot" invisible={!isActive}>
          <IconButton onClick={() => setIsOpen((prev) => !prev)} aria-label="Toggle filters">
            <FilterListIcon />
          </IconButton>
        </Badge>
        <Typography variant="body2">Filters</Typography>
      </Stack>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <Paper sx={{ mt: 1, p: 2 }}>
          <Stack spacing={2}>
            <FormProvider {...methods}>
              <Form
                onSubmit={methods.handleSubmit(handleSubmit)}
                onChange={handleChange}
                requireDirtyForChange={false}
              >

                <Typography variant="subtitle1" sx={{mb: 2}}>Filters</Typography>
                <Grid container spacing={2}>
                  {filterOptions.map((option) => {
                    const label = option.label || option.key;
                    const placeholder = option.placeholder || option.key;

                    return (
                      <Grid xs={12} sm={6} md={4} key={String(option.key)}>
                        {option.type === 'text' && <FormTextField name={option.key} label={label} placeholder={placeholder} />}
                        {option.type === 'single-select' && <FormDropDown name={option.key} label={label} list={option.list || []} />}
                        {option.type === 'multi-select' && <FormMultiSelectChips name={option.key} label={label} list={option.list || []} enableSelectAll />}
                        {(option.type === 'datetime' || option.type === 'datetime-after' || option.type === 'datetime-before') && <FormDateTimePicker name={option.key} label={label} required={false} slotProps={{ textField: { fullWidth: true } }} />}
                        {(option.type === 'date' || option.type === 'date-after' || option.type === 'date-before') && <FormDatePicker name={option.key} label={label} required={false} slotProps={{ textField: { fullWidth: true } }} />}
                      </Grid>
                    );
                  })}
                </Grid>

                <Divider sx={{my: 2}} />

                <Typography variant="subtitle1" sx={{mb: 2}}>Sorting</Typography>
                <Grid container spacing={2}>
                  <Grid xs={12} sm={4}>
                    <FormDropDown name="__sortkey" label="Sort Field" list={sortkeyItems} />
                  </Grid>
                  <Grid xs={12} sm={4}>
                    <FormDropDown name="__sortdirection" label="Sort Direction" list={[{value: 'asc', label: 'Ascending (a-z,0-9)'}, {value: 'desc', label: 'Descending (z-a,9-0)'}]} />
                  </Grid>
                  <Grid xs={12}>
                    <Row>
                      <FormButton onClick={resetFilters}>Reset</FormButton>
                    </Row>
                  </Grid>
                </Grid>

              </Form>
            </FormProvider>
          </Stack>
        </Paper>
      </Collapse>
    </Box>
  );
}

export default Filter;
